/**
 * Landing — public marketing page (Vooice v3).
 *
 * Composes Hero (animated orb + transcript glass), CapabilityGrid (7 cells),
 * Ticker (auto-scrolling receipts), Boundaries (6 refusals), FinalCTA, Footer.
 *
 * Auth-aware: "Sign in" / "Open app" CTA in the top nav swaps based on
 * current session state. Single useAudioPulse RAF drives the kinetic
 * elements (orb, transcript wave) so render cost is one frame per tick.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { Mono } from "../components/v3";

// ── Tokens / utilities ────────────────────────────────────────────────────

const cx = (...c: (string | false | undefined)[]) => c.filter(Boolean).join(" ");

function useAudioPulse(speed = 1) {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf: number;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      setT((v) => v + dt * speed);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [speed]);
  return t;
}

function ampAt(t: number, seed = 0) {
  const a =
    0.5 +
    0.5 * Math.sin(t * 1.7 + seed * 0.7) * 0.55 +
    0.5 * Math.sin(t * 2.9 + seed * 1.3) * 0.3 +
    0.5 * Math.sin(t * 5.3 + seed * 2.1) * 0.15;
  return Math.max(0, Math.min(1, a));
}

// ── Page ──────────────────────────────────────────────────────────────────

export function Landing() {
  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="stage" aria-hidden="true" />
      <Nav />
      <main className="relative z-10">
        <Hero />
        <CapabilityGrid />
        <Ticker />
        <Boundaries />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}

// ── Nav ──────────────────────────────────────────────────────────────────

function Nav() {
  const { authenticated, loading } = useAuth();
  const ctaTo = authenticated ? "/app/home" : "/login";
  const ctaLabel = authenticated ? "Open app" : "Sign in";

  return (
    <header className="fixed top-0 inset-x-0 z-50 px-6 md:px-8 py-5 grid grid-cols-[1fr_auto_1fr] items-center bg-gradient-to-b from-bg/90 to-transparent backdrop-blur-md">
      <Link to="/" className="inline-flex items-center gap-2.5">
        <BrandMark />
        <span className="font-display text-[18px] tracking-tight font-medium">
          Vooice
        </span>
      </Link>

      <div className="hidden md:flex items-center gap-3 justify-self-center">
        <span className="size-1.5 rounded-full bg-amber animate-pulse-amber shadow-[0_0_14px_rgba(255,179,112,0.55)]" />
        <Mono size="sm" tone="t2">
          An operator on retainer · Live
        </Mono>
      </div>

      <div className="flex items-center gap-2 justify-self-end">
        <a href="#how" className="hidden sm:inline-flex btn-ghost-sm">
          How it works
        </a>
        {!loading && (
          <Link to={ctaTo} className="btn-amber-sm">
            {ctaLabel}
          </Link>
        )}
      </div>

      {/* Inline button styles — keeps Landing self-contained */}
      <style>{`
        .btn-ghost-sm {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 9px 14px; border-radius: 999px;
          font-size: 12px; font-weight: 500; letter-spacing: -0.01em;
          background: transparent; color: var(--t1);
          border: 1px solid var(--line);
          transition: border-color .2s, background-color .2s;
        }
        .btn-ghost-sm:hover { border-color: rgba(242,234,211,0.32); background: rgba(242,234,211,0.04); }
        .btn-amber-sm {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 10px 16px; border-radius: 999px;
          font-size: 12px; font-weight: 600; letter-spacing: -0.01em;
          background: var(--amber); color: #2A1A0E; border: 1px solid var(--amber);
          box-shadow: 0 1px 0 rgba(255,255,255,0.55) inset, 0 18px 50px -10px rgba(255,179,112,0.55);
          transition: transform .15s, box-shadow .25s;
        }
        .btn-amber-sm:hover { transform: translateY(-1px); box-shadow: 0 1px 0 rgba(255,255,255,0.6) inset, 0 26px 70px -10px rgba(255,179,112,0.55); }
        .btn-amber-lg {
          display: inline-flex; align-items: center; gap: 10px;
          padding: 18px 30px; border-radius: 999px;
          font-size: 16px; font-weight: 600; letter-spacing: -0.01em;
          background: var(--amber); color: #2A1A0E; border: 1px solid var(--amber);
          box-shadow: 0 1px 0 rgba(255,255,255,0.55) inset, 0 18px 50px -10px rgba(255,179,112,0.55);
          transition: transform .15s, box-shadow .25s;
        }
        .btn-amber-lg:hover { transform: translateY(-1px); box-shadow: 0 1px 0 rgba(255,255,255,0.6) inset, 0 26px 70px -10px rgba(255,179,112,0.55); }
        .btn-ghost-lg {
          display: inline-flex; align-items: center; gap: 10px;
          padding: 18px 30px; border-radius: 999px;
          font-size: 16px; font-weight: 500; letter-spacing: -0.01em;
          background: transparent; color: var(--t1);
          border: 1px solid var(--line);
          transition: border-color .2s, background-color .2s;
        }
        .btn-ghost-lg:hover { border-color: rgba(242,234,211,0.32); background: rgba(242,234,211,0.04); }
      `}</style>
    </header>
  );
}

function BrandMark() {
  const t = useAudioPulse(0.5);
  const env = ampAt(t, 0);
  return (
    <svg
      width="30"
      height="22"
      viewBox="0 0 56 40"
      fill="none"
      className="text-amber"
      aria-hidden="true"
    >
      <path
        d={`M2 14 L10 ${28 - env * 4} L18 ${14 + env * 6} L26 30 L34 ${14 + env * 6} L42 ${28 - env * 4} L54 14`}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative min-h-screen flex flex-col justify-center px-6 md:px-8 pt-32 pb-16">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
        <HeroOrb />
      </div>

      <div className="max-w-[1280px] mx-auto w-full relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-[1.15fr_0.85fr] gap-10 md:gap-16 items-end">
          <div className="relative z-20">
            <div className="inline-flex items-center gap-3">
              <span className="size-1.5 rounded-full bg-amber shadow-[0_0_16px_rgba(255,179,112,0.55)] animate-pulse-amber" />
              <Mono size="sm" tone="t2">
                An operator on retainer · Live
              </Mono>
            </div>

            <h1
              className="mt-5 font-display leading-[0.95] text-[clamp(56px,10vw,140px)]"
              style={{
                fontVariationSettings: '"opsz" 144, "SOFT" 60, "wght" 360',
                textShadow: "0 1px 0 rgba(0,0,0,0.5)",
              }}
            >
              <span className="block">Your Vooice</span>
              <span className="block">takes calls</span>
              <span
                className="block text-amber italic"
                style={{
                  fontVariationSettings:
                    '"opsz" 144, "SOFT" 100, "wght" 380',
                }}
              >
                so you don&apos;t.
              </span>
            </h1>

            <p className="mt-7 text-[17px] md:text-[18px] text-t2 max-w-[52ch] leading-relaxed">
              A private operator who books, declines, confirms and follows up —
              using your calendar, your contacts, your rules.{" "}
              <span
                className="font-display italic text-t1"
                style={{ fontVariationSettings: '"opsz" 144, "SOFT" 100' }}
              >
                She introduces herself as AI, every time.
              </span>
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/signup" className="btn-amber-lg">
                Hire her — first month free
              </Link>
              <a href="#how" className="btn-ghost-lg">
                Hear her ↓
              </a>
            </div>

            <div className="mt-4 mono-caps">
              Cancel by saying so · No app required
            </div>
          </div>

          <div className="relative z-20">
            <TranscriptGlass />
          </div>
        </div>

        {/* Stat row */}
        <div className="mt-14 pt-7 border-t border-line-soft grid grid-cols-2 md:grid-cols-4 gap-x-12 gap-y-6 max-w-[800px]">
          <Stat num="312k" label="Calls placed in 2025" emphasizeNum />
          <Stat num="94" suffix="%" label="Booked on first try" emphasizeSuffix />
          <Stat num="00:38" suffix="s" label="Median call length" emphasizeSuffix />
          <Stat num="0" label="Pretending to be human" emphasizeNum />
        </div>
      </div>
    </section>
  );
}

function Stat({
  num,
  suffix,
  label,
  emphasizeNum,
  emphasizeSuffix,
}: {
  num: string;
  suffix?: string;
  label: string;
  emphasizeNum?: boolean;
  emphasizeSuffix?: boolean;
}) {
  return (
    <div>
      <div className="font-display text-[28px] leading-none tracking-tight">
        <span className={emphasizeNum ? "italic text-amber" : ""}>{num}</span>
        {suffix && (
          <span className={emphasizeSuffix ? "italic text-amber" : ""}>{suffix}</span>
        )}
      </div>
      <div className="mono-caps mt-1">{label}</div>
    </div>
  );
}

// ── HeroOrb ──────────────────────────────────────────────────────────────
// Big animated amber orb — concentric rings + radiating waveform bars + glowing core.
// Heavy SVG, but a focal element. Reduced-motion users see a still version.

function HeroOrb() {
  const t = useAudioPulse(0.9);
  const env = ampAt(t, 0);

  const rings = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const baseR = 140 + i * 70;
      const localEnv = ampAt(t + i * 0.4, i);
      const r = baseR + localEnv * 22;
      const opacity = 0.42 - i * 0.05;
      arr.push({ r, opacity, key: i });
    }
    return arr;
  }, [t]);

  const bars = useMemo(() => {
    const arr = [];
    const barCount = 56;
    for (let i = 0; i < barCount; i++) {
      const a = (i / barCount) * Math.PI * 2;
      const localEnv = ampAt(t * 1.3 + i * 0.18, i);
      const len = 14 + localEnv * 38;
      const r1 = 96;
      const r2 = r1 + len;
      const x1 = 500 + Math.cos(a) * r1;
      const y1 = 500 + Math.sin(a) * r1;
      const x2 = 500 + Math.cos(a) * r2;
      const y2 = 500 + Math.sin(a) * r2;
      arr.push({ x1, y1, x2, y2, key: i, op: 0.28 + localEnv * 0.5 });
    }
    return arr;
  }, [t]);

  return (
    <div
      className="w-[min(1100px,100vw)] aspect-square translate-x-[8%] translate-y-[4%] md:opacity-100 opacity-50"
      style={{ filter: "drop-shadow(0 40px 120px rgba(255,179,112,0.18))" }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 1000 1000" className="w-full h-full block">
        <defs>
          <radialGradient id="coreGrad" cx="40%" cy="38%" r="60%">
            <stop offset="0%" stopColor="#FFE8CF" stopOpacity="1" />
            <stop offset="40%" stopColor="#FFB370" stopOpacity="0.85" />
            <stop offset="78%" stopColor="#8E4F22" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#0F0D10" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="haloGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFB370" stopOpacity="0.45" />
            <stop offset="55%" stopColor="#5B3D6E" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#0F0D10" stopOpacity="0" />
          </radialGradient>
          <filter id="softBlur" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="6" />
          </filter>
        </defs>
        <circle cx="500" cy="500" r={460 + env * 14} fill="url(#haloGrad)" />
        {rings.map((r) => (
          <circle
            key={r.key}
            cx="500"
            cy="500"
            r={r.r}
            fill="none"
            stroke="rgba(255,179,112,1)"
            strokeOpacity={r.opacity}
            strokeWidth={r.key === 0 ? 1.4 : 0.7}
          />
        ))}
        {bars.map((b) => (
          <line
            key={b.key}
            x1={b.x1}
            y1={b.y1}
            x2={b.x2}
            y2={b.y2}
            stroke="#FFB370"
            strokeOpacity={b.op}
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        ))}
        <circle
          cx="500"
          cy="500"
          r={88 + env * 6}
          fill="url(#coreGrad)"
          filter="url(#softBlur)"
        />
        <circle cx="500" cy="500" r={64 + env * 4} fill="url(#coreGrad)" />
        <ellipse cx="478" cy="478" rx="18" ry="10" fill="rgba(255,255,255,0.7)" opacity="0.7" />
      </svg>
    </div>
  );
}

// ── Transcript glass card ────────────────────────────────────────────────

const TRANSCRIPT_LOOP = [
  { who: "her", text: "Hi, this is Vooice — an AI assistant calling on behalf of Marcus Wu." },
  { who: "them", text: "Oh, hi. What did you need?" },
  { who: "her", text: "A 45-minute cut, Thursday after two if you have it." },
  { who: "them", text: "I can do 2:15. That work?" },
  { who: "her", text: "Two-fifteen Thursday — putting it on his calendar now." },
];

function TranscriptGlass() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setStep((s) => (s + 1) % TRANSCRIPT_LOOP.length),
      2400,
    );
    return () => clearInterval(id);
  }, []);

  const visible = useMemo(() => {
    const arr = [];
    for (let i = 0; i <= step; i++)
      arr.push({ ...TRANSCRIPT_LOOP[i], key: `${step}-${i}` });
    return arr.slice(-3);
  }, [step]);

  return (
    <div className="relative w-full max-w-[460px] ml-auto glass rounded-[22px] p-5 overflow-hidden">
      <div className="flex justify-between items-center pb-2.5 mb-3 border-b border-line">
        <Mono size="sm" tone="t3">Live · Out 415-555-0144</Mono>
        <div className="inline-flex items-end gap-[2px] h-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <span
              key={i}
              className="w-[2px] bg-amber rounded-[1px] animate-wave-tiny"
              style={{ animationDelay: `${i * 0.1}s` }}
            />
          ))}
        </div>
      </div>

      {visible.map((line) => (
        <span
          key={line.key}
          className={cx(
            "block py-2 leading-snug animate-rise-in",
            line.who === "her"
              ? "font-display italic text-t1 text-[16px]"
              : "font-sans text-t2 text-[15px]",
          )}
          style={
            line.who === "her"
              ? { fontVariationSettings: '"opsz" 36, "SOFT" 80' }
              : undefined
          }
        >
          {line.who === "them" && (
            <span className="text-t3 mr-1">↳</span>
          )}
          {line.text}
        </span>
      ))}

      <div className="pt-3 mt-2 border-t border-line flex justify-between">
        <Mono>Vooice · 00:0{Math.min(9, step + 2)}</Mono>
        <Mono tone="amber">● Booking</Mono>
      </div>
    </div>
  );
}

// ── Capability grid ──────────────────────────────────────────────────────

const CAPS: Array<{
  kind: "large" | "tall" | "med" | "wide";
  num: string;
  title: string;
  body: string;
  art?: "wave" | "tags" | "stack";
}> = [
  {
    kind: "large",
    num: "01",
    title: "Outbound calls, identified as AI",
    body: "She places the call, says she is an AI, says whose behalf she is on. Repeats every commitment back before hanging up.",
    art: "wave",
  },
  {
    kind: "tall",
    num: "02",
    title: "Inbound screening",
    body: "Strangers, robocallers, the dentist confirming again — she takes them.",
    art: "tags",
  },
  {
    kind: "med",
    num: "03",
    title: "Calendar, alive",
    body: "Reads, writes, holds tentatives. Sees what is real and what is a placeholder.",
  },
  {
    kind: "wide",
    num: "04",
    title: "Memory you can correct",
    body: "Names, allergies, your hairdresser is at Bayview not Bayhill. Edit it like a paragraph.",
    art: "stack",
  },
  {
    kind: "med",
    num: "05",
    title: "Voice notes back",
    body: "A short audio recap when text would feel cold.",
  },
  {
    kind: "med",
    num: "06",
    title: "SMS & WhatsApp",
    body: "Same operator, on every surface that rings.",
  },
  {
    kind: "med",
    num: "07",
    title: "Receipts of everything",
    body: "A timestamped log. Searchable. Yours to keep.",
  },
];

function CapabilityGrid() {
  return (
    <section
      id="how"
      className="relative px-6 md:px-8 py-24 md:py-40 border-t border-line-soft"
    >
      <div className="max-w-[1280px] mx-auto">
        <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-8 mb-16">
          <div>
            <Mono className="block mb-3">What she does · Seven things, well</Mono>
            <h2 className="font-display text-[clamp(40px,7vw,80px)] leading-[1.05]">
              Seven things. <span className="font-display-italic">Exceptionally.</span>
            </h2>
          </div>
          <p className="text-t2 text-[16px] max-w-[36ch] leading-relaxed">
            She is not a phone tree, not a sidekick, not your assistant&apos;s
            assistant. One operator on retainer who answers, calls, declines,
            books and remembers — and tells you what happened.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-12 auto-rows-[minmax(160px,auto)] gap-4">
          {CAPS.map((c) => (
            <CapCell key={c.num} {...c} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CapCell({
  kind,
  num,
  title,
  body,
  art,
}: {
  kind: "large" | "tall" | "med" | "wide";
  num: string;
  title: string;
  body: string;
  art?: "wave" | "tags" | "stack";
}) {
  const span = {
    large: "col-span-2 md:col-span-6 md:row-span-2",
    tall: "col-span-2 md:col-span-3 md:row-span-2",
    wide: "col-span-2 md:col-span-6",
    med: "col-span-2 md:col-span-3",
  }[kind];

  return (
    <article
      className={cx(
        "relative rounded-2xl border border-line p-7 flex flex-col gap-3 justify-between overflow-hidden transition-colors",
        "bg-gradient-to-b from-white/[0.022] to-white/[0.005]",
        "hover:border-t4",
        kind === "large" && "before:pointer-events-none before:absolute before:inset-0 before:bg-[radial-gradient(70%_60%_at_80%_0%,rgba(255,179,112,0.14),transparent_60%)]",
        span,
      )}
    >
      <div className="relative z-10 flex justify-between items-start">
        <Mono>{num}</Mono>
      </div>
      <div className="relative z-10">
        <h4
          className={cx(
            "font-display tracking-tight",
            kind === "large" ? "text-[32px] max-w-[16ch]" : "text-[22px] max-w-[22ch]",
          )}
          style={{ fontVariationSettings: '"opsz" 36, "SOFT" 70' }}
        >
          {title}
        </h4>
        <p
          className={cx(
            "text-t2 mt-2 leading-relaxed",
            kind === "large" ? "text-[16px] max-w-[40ch]" : "text-[14px] max-w-[32ch]",
          )}
        >
          {body}
        </p>
      </div>
      {art && (
        <div className="relative z-10 mt-2">
          <CapArt kind={art} />
        </div>
      )}
    </article>
  );
}

function CapArt({ kind }: { kind: "wave" | "tags" | "stack" }) {
  const t = useAudioPulse(0.9);
  if (kind === "wave") {
    return (
      <div className="h-[60px] flex items-end gap-1">
        {Array.from({ length: 32 }).map((_, i) => {
          const env = ampAt(t + i * 0.18, i);
          return (
            <span
              key={i}
              className="flex-1 bg-gradient-to-t from-amber/20 to-amber rounded-[2px] opacity-85"
              style={{ height: `${10 + env * 90}%` }}
            />
          );
        })}
      </div>
    );
  }
  if (kind === "tags") {
    const tags = ["Unknown", "Robocall", "Sales", "Reschedule", "Spam", "Vendor"];
    return (
      <div className="flex flex-wrap gap-1.5">
        {tags.map((tag) => (
          <span
            key={tag}
            className="font-mono text-[10px] tracking-[0.10em] uppercase text-t2 px-2.5 py-1 rounded-pill border border-line bg-white/[0.02]"
          >
            {tag}
          </span>
        ))}
      </div>
    );
  }
  if (kind === "stack") {
    return (
      <div className="flex flex-col gap-1.5">
        {[
          ["hairdresser", "Bayview"],
          ["allergy", "penicillin"],
          ["partner", "Lina"],
        ].map(([k, v]) => (
          <div
            key={k}
            className="font-mono text-[11px] text-t2 px-2.5 py-2 rounded-md bg-white/[0.02] border border-line-soft flex justify-between"
          >
            <span>{k}</span>
            <span className="text-amber">{v}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
}

// ── Receipts ticker ──────────────────────────────────────────────────────

const RECEIPTS: Array<[string, string, string, "ok" | "bad"]> = [
  ["BAYVIEW HAIR", "BOOKED", "Thu 2:15 PM", "ok"],
  ["DR KO · DENTIST", "CONFIRMED", "Tomorrow 10 AM", "ok"],
  ["WESTFIELD REALTY", "DECLINED", "00:38", "bad"],
  ["MERIDIAN BANK", "SCREENED", "00:12", "ok"],
  ["LINA", "TEXTED", "Running 5 late", "ok"],
  ["UNKNOWN · 415", "BLOCKED", "Robocall", "bad"],
  ["MARCUS — MOM", "CALLBACK", "After 6 PM", "ok"],
  ["VET · COASTAL", "BOOKED", "Sat 11:00 AM", "ok"],
  ["INSURANCE", "DEFERRED", "Needs you", "bad"],
  ["PT · COREY", "CONFIRMED", "Tue 7 AM", "ok"],
  ["SCHOOL · NORA", "NOTED", "Permission slip", "ok"],
  ["HVAC · NORTHRIDGE", "BOOKED", "Fri 9–11 AM", "ok"],
];

function Ticker() {
  const items = [...RECEIPTS, ...RECEIPTS];
  return (
    <div
      className="relative border-y border-line-soft py-7 overflow-hidden bg-gradient-to-b from-white/[0.012] to-transparent"
      aria-hidden="true"
    >
      {/* Edge fades */}
      <div className="absolute inset-y-0 left-0 w-[120px] bg-gradient-to-r from-bg to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-[120px] bg-gradient-to-l from-bg to-transparent z-10 pointer-events-none" />

      <div className="ticker-track flex gap-14 w-max">
        {items.map(([name, verb, when, kind], i) => (
          <span
            key={i}
            className="inline-flex items-center gap-3.5 font-mono text-[12px] tracking-[0.1em] text-t2 whitespace-nowrap"
          >
            <span className={kind === "ok" ? "text-amber" : "text-rose"}>●</span>
            <span className="text-t1 uppercase tracking-[0.18em] text-[11px]">{name}</span>
            <span className="text-t4">—</span>
            <span className="text-t3 uppercase tracking-[0.18em] text-[11px]">{verb}</span>
            <span className="text-t3">{when}</span>
          </span>
        ))}
      </div>

      <style>{`
        @keyframes ticker { to { transform: translateX(-50%); } }
        .ticker-track { animation: ticker 80s linear infinite; }
        .ticker-track:hover { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .ticker-track { animation: none; } }
      `}</style>
    </div>
  );
}

// ── Boundaries ───────────────────────────────────────────────────────────

const BOUNDS = [
  {
    num: "I",
    head: "She tells everyone she is AI.",
    body: "In the first sentence, every call. There is no incognito mode. We will not build that toggle.",
  },
  {
    num: "II",
    head: "She does not deceive on your behalf.",
    body: "No fake voices, no impersonation, no pretending to be you. If they ask if she is human, she says no.",
  },
  {
    num: "III",
    head: "She refuses the heavy things.",
    body: "Disputes about money, custody, medicine, grief. Those calls belong to a human who can carry them.",
  },
  {
    num: "IV",
    head: "She will not sell, persuade or pressure.",
    body: "No outbound marketing. No collections. No follow-ups designed to wear someone down.",
  },
  {
    num: "V",
    head: "She forgets when you tell her to.",
    body: "A memory is one button. Forget Lina, forget the Tuesday rule, forget last week. Done.",
  },
  {
    num: "VI",
    head: "She does not record by default.",
    body: "Transcripts, yes. Audio of the other party only when they consent and the law requires.",
  },
];

function Boundaries() {
  return (
    <section className="px-6 md:px-8 py-24 md:py-40 border-t border-line-soft bg-gradient-to-b from-transparent via-plum/10 to-transparent">
      <div className="max-w-[1280px] mx-auto">
        <div className="max-w-[720px] mb-20">
          <Mono className="block mb-3">What she will not do · Six refusals</Mono>
          <h2 className="font-display text-[clamp(48px,7vw,96px)] leading-[1.05]">
            A short list of <span className="font-display-italic">nos</span>.
          </h2>
          <p className="mt-7 text-[18px] text-t2 leading-relaxed max-w-[50ch]">
            The product is partly defined by what we won&apos;t build. Read these
            the way you&apos;d read a handshake — these are the things she refuses,
            on purpose.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {BOUNDS.map((b) => (
            <article
              key={b.num}
              className="relative rounded-[18px] border border-line p-7 bg-gradient-to-b from-white/[0.022] to-white/[0.005] flex flex-col gap-2.5 transition-transform hover:-translate-y-0.5"
            >
              <span
                className="absolute top-5 right-5 size-7 rounded-full flex items-center justify-center bg-plum/20 text-rose text-xs"
                aria-hidden="true"
              >
                ✕
              </span>
              <Mono>{b.num}</Mono>
              <h4
                className="font-display text-[22px] tracking-tight max-w-[22ch] mt-1"
                style={{ fontVariationSettings: '"opsz" 36, "SOFT" 70' }}
              >
                {b.head}
              </h4>
              <p className="text-t2 text-[14px] leading-relaxed max-w-[42ch]">
                {b.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Final CTA ────────────────────────────────────────────────────────────

function FinalCTA() {
  const { authenticated } = useAuth();
  return (
    <section
      id="begin"
      className="relative text-center px-6 md:px-8 py-40 md:py-56"
    >
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[1100px] pointer-events-none rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(255,179,112,0.16), transparent 70%)",
          filter: "blur(20px)",
        }}
        aria-hidden="true"
      />
      <Mono className="relative">Begin</Mono>
      <h2 className="relative font-display text-[clamp(56px,9vw,140px)] leading-[1.05] max-w-[14ch] mx-auto mt-6">
        Hand off the phone.{" "}
        <span className="font-display-italic">Keep the day.</span>
      </h2>
      <p className="relative text-t2 text-[18px] max-w-[50ch] mx-auto mt-7">
        The first month is on us. Cancel any time, by saying so — not by
        clicking through five screens.
      </p>
      <div className="relative mt-12 inline-flex items-center gap-3.5 flex-wrap justify-center">
        <Link to={authenticated ? "/app/home" : "/signup"} className="btn-amber-lg">
          {authenticated ? "Open app" : "Hire her"}
        </Link>
        <a href="#how" className="btn-ghost-lg">
          Hear her again
        </a>
      </div>
    </section>
  );
}

// ── Footer ──────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="relative z-10 border-t border-line px-6 md:px-8 py-10 md:py-12 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-6 items-center text-t3 font-mono text-[10px] uppercase tracking-[0.18em]">
      <span>© 2026 Vooice · An operator on retainer</span>
      <span className="text-center hidden md:inline">
        She introduces herself as AI · Every call · Every time
      </span>
      <span className="md:text-right">San Francisco · MMXXVI</span>
    </footer>
  );
}
